import { enSimulation } from "../lib/simulation";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const jeton = () => localStorage.getItem("admin_token");

/* Le mode école (/direction) partage ces routes avec la console : il se signale, pour que
   le serveur refuse ce qui ne doit se faire que dans la console (les jours d'un stage, qui
   refont la facture — école, 09.10.2026). */
const canal = () => (typeof window !== "undefined" && window.location.pathname.startsWith("/direction")
  ? { "X-Console-Canal": "ecole" } : {});

export async function api(chemin, options = {}) {
  // Un envoi avec pièce jointe passe en FormData : c'est le navigateur qui doit écrire le
  // Content-Type (il y met la frontière du multipart). L'imposer casserait le découpage.
  const fichier = options.body instanceof FormData;
  const res = await fetch(`${BASE}/api/console${chemin}`, {
    ...options,
    headers: {
      ...(fichier ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${jeton()}`,
      ...canal(),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    // Session expirée : on ramène directement l'écran de connexion. L'ancien message
    // rouge « Session expirée, reconnecte-toi » laissait l'école bloquée, sans formulaire
    // ni bouton de déconnexion visible (surtout sur mobile).
    localStorage.removeItem("admin_token");
    window.dispatchEvent(new CustomEvent("console-session-expiree"));
    throw new Error("session");
  }
  if (!res.ok) {
    // Le message du serveur explique la panne (« Ce sont les deux mêmes fiches ») : sans
    // lui, l'école ne lisait qu'« Erreur 400 » et ne pouvait rien en faire.
    let detail = "";
    try { detail = ((await res.json()) || {}).detail || ""; } catch { detail = ""; }
    throw new Error(typeof detail === "string" && detail ? detail : `Erreur ${res.status}`);
  }

  return res.json();
}

/* « Continuer avec Google » (Jen, 02.11.2026) : la même connexion Google que l'app ; seuls
   les comptes de la console entrent (adresse du compte ou « adresse de l'app »). */
export const urlConnexionGoogle = () =>
  `${BASE}/api/espace/google/debut?retour=${encodeURIComponent(window.location.origin)}&pour=console`;
export const googleActif = () =>
  fetch(`${BASE}/api/espace/google/actif`).then((r) => (r.ok ? r.json() : {})).then((d) => !!d.actif).catch(() => false);

export async function connexion(form) {
  const res = await fetch(`${BASE}/api/admin/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(form),
  });
  if (!res.ok) return null;
  const data = await res.json();
  localStorage.setItem("admin_token", data.access_token);
  return data;
}

/* La console reste ouverte toute la journée à l'école : on prolonge la session en
   silence (toutes les 30 min et au retour sur l'onglet) pour ne plus jamais afficher
   « Session expirée » devant quelqu'un qui travaille. */
export function garderLaSession() {
  // Le simulateur de la console n'a pas de session à prolonger (lib/simulation.js).
  if (enSimulation()) return () => {};
  let dernier = 0;
  const prolonger = async () => {
    if (!jeton() || Date.now() - dernier < 60_000) return;
    dernier = Date.now();
    try {
      const res = await fetch(`${BASE}/api/admin/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jeton()}` },
      });
      if (res.ok) localStorage.setItem("admin_token", (await res.json()).access_token);
    } catch { /* hors ligne : on réessaiera au prochain réveil */ }
  };
  const minuterie = setInterval(prolonger, 30 * 60_000);
  const reveil = () => { if (document.visibilityState === "visible") prolonger(); };
  document.addEventListener("visibilitychange", reveil);
  prolonger();
  return () => { clearInterval(minuterie); document.removeEventListener("visibilitychange", reveil); };
}

/* Relire un PDF : il s'affiche DANS la console. Un window.open() après un await n'est
   plus rattaché au clic, et les navigateurs le bloquent silencieusement — c'est ce qui
   donnait l'impression que la facture ne s'ouvrait pas. */
export function ouvrirPdf(chemin, titre = "") {
  window.dispatchEvent(new CustomEvent("console-pdf", { detail: { chemin, titre } }));
}

export async function chargerPdf(chemin) {
  const res = await fetch(`${BASE}/api/console${chemin}`, {
    headers: { Authorization: `Bearer ${jeton()}` },
  });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json()).detail || ""; } catch { /* pas de corps JSON */ }
    throw new Error(detail || `Erreur ${res.status}`);
  }
  return URL.createObjectURL(await res.blob());
}

export const argent = (n) =>
  `${new Intl.NumberFormat("fr-CH", { maximumFractionDigits: 0 }).format(n || 0)} CHF`;

export const dateCourte = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
};

/* Téléchargement d'un fichier protégé (extrait comptable, export) : même en-tête
   d'authentification que le reste de la console. */
export async function telecharger(chemin, nom) {
  const res = await fetch(`${BASE}/api/console${chemin}`, {
    headers: { Authorization: `Bearer ${jeton()}` },
  });
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  const url = URL.createObjectURL(await res.blob());
  const lien = document.createElement("a");
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}
